import {formatUsPhoneDisplay, normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";
import {Property} from "@/src/modules/properties/types";
import {getResidentMembershipRoleLabel} from "@/src/modules/core/lib/resident-membership";

import {RoleSchema, User, UserHouseholdMembership, UserSchema} from "../types";
import {normalizeDispatchCenterOption} from "./normalize-dispatch-center-options";

/** Sorted id list for comparing server-loaded vs form property assignments on update. */
export function propertyIdsSignature(items: {id: number | string}[] | undefined | null): string {
  if (!items?.length) return "";
  return [...items.map((p) => Number(p.id))].sort((a, b) => a - b).join(",");
}

export function normalizeFormProperties(
  value: UserSchema["properties"],
): Pick<Property, "id" | "name">[] {
  if (value == null) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr.filter(
    (p): p is Pick<Property, "id" | "name"> =>
      Boolean(p && typeof p === "object" && "id" in p),
  );
}

/** Standing property links are not allowed (`forbidden`) or come from session (`all`). */
export function rolePropertyAssignmentForbidden(
  role: Pick<RoleSchema, "without_property"> | null | undefined,
): boolean {
  return role?.without_property === "forbidden";
}

/** When `all` or `forbidden`, hide property assignment UI. Residents are property-scoped via household onboarding. */
export function roleShowsPropertyAssignment(
  role: Pick<RoleSchema, "without_property" | "name"> | null | undefined,
): boolean {
  if (!role || isResidentRole(role)) return false;
  return role.without_property !== "all" && role.without_property !== "forbidden";
}

export function roleRequiresPropertyAssignment(
  role: Pick<RoleSchema, "without_property" | "name"> | null | undefined,
): boolean {
  if (isResidentRole(role)) return false;
  return role?.without_property === "required";
}

export function formatUserMembershipLabel(membership: UserHouseholdMembership): string {
  const household = membership.house_hold_name?.trim();
  const property = membership.property_name?.trim();
  if (household && property) return `${household} · ${property}`;
  return household || property || `Household ${membership.house_hold_id}`;
}

export function formatUserMembershipDisplay(membership: UserHouseholdMembership): string {
  return `${getResidentMembershipRoleLabel(membership)} · ${formatUserMembershipLabel(membership)}`;
}

/** First active membership — replaces legacy `user.house_hold` reads. */
export function getUserPrimaryHouseholdRef(
  user: Pick<User, "memberships"> | null | undefined,
): {id: number; name: string} | null {
  const membership = user?.memberships?.[0];
  if (!membership?.house_hold_id) return null;
  const name = membership.house_hold_name?.trim() || `Household ${membership.house_hold_id}`;
  return {id: membership.house_hold_id, name};
}

export function getUserPrimaryHouseholdId(
  user: Pick<User, "memberships"> | null | undefined,
): number | null {
  const id = getUserPrimaryHouseholdRef(user)?.id;
  return id != null && id !== 0 ? id : null;
}

export function isResidentRole(
  role: Pick<RoleSchema, "name"> | null | undefined,
): boolean {
  return role?.name?.trim().toLowerCase() === "resident";
}

/** Role name from API, or fixed copy when `role` is null. */
export function getUserRoleLabel(user: User): string {
  const name = user.role?.name?.trim();
  return name ? name : "Missing Role";
}

export function getUserEmergencyContactDisplay(user: User): {
  name: string | null;
  phone: string | null;
} {
  const name = user.emergency_contact_name?.trim() || null;
  const rawPhone = user.emergency_contact_phone?.trim();
  const phone =
    rawPhone && formatUsPhoneDisplay(rawPhone) !== "-" ? formatUsPhoneDisplay(rawPhone) : null;
  return {name, phone};
}

export function getSheetDefaultValues(user: User | null) {
  // if (!user) return undefined;

  return {
    role: user?.role ? (user.role as unknown as RoleSchema) : null,
    email: user?.email ?? "",
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    user_name: user?.user_name ?? "",
    telephone: normalizeUsPhoneDigits(user?.telephone ?? ""),
    emergency_contact_name: user?.emergency_contact_name ?? "",
    emergency_contact_phone: normalizeUsPhoneDigits(user?.emergency_contact_phone ?? ""),
    password: "",
    properties: user?.properties?.map((p) => ({id: Number(p.id), name: p.name})) ?? [],
    dispatch_center: user?.dispatch_center
      ? normalizeDispatchCenterOption(user.dispatch_center)
      : null,
  };
}
